import { User, Customer, ClientUser } from '@/contexts/auth/types';
import { toClientUser } from '@/contexts/auth/utils/userUtils';
import { TOKEN_EXPIRY_MINUTES } from '@/lib/authConstants';
import api from './api/core';
import { getUsers } from './api/users';
import { getCustomers } from './api/customers';
import { getTokenPair, refreshAuthToken, verifyAuthToken, getCurrentUser } from './api/auth';

interface LoginResponse {
  access: string;
  refresh: string;
}

interface UserResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_superuser: boolean;
  last_active_customer?: string;
  customer_access: CustomerAccess[];
  profile?: {
    theme?: 'dark' | 'light' | 'auto';
    last_active_customer?: string;
    last_active_site?: number | null;
  };
}

interface CustomerAccess {
  customer: Customer;
  is_admin: boolean;
}

export const loginUser = async (email: string, password: string): Promise<{
  access: string;
  refresh: string;
  user: User;
  customers: Customer[];
}> => {
  try {
    // Step 1: Get token pair using the auth API
    const tokenData = await getTokenPair(email, password);
    const { access, refresh } = tokenData;
    
    // Set authorization header for next request
    api.defaults.headers.common['Authorization'] = `Bearer ${access}`;
    
    // Step 2: Fetch user data using the auth API
    const userData = await getCurrentUser();
    
    // Extract user info including is_superuser
    const user: User = {
      id: userData.id,
      email: userData.email,
      first_name: userData.first_name || '',
      last_name: userData.last_name || '',
      last_active_customer: userData.last_active_customer,
      is_superuser: userData.is_superuser,
      profile: userData.profile || { theme: 'auto' } // Ensure profile exists with default theme
    };
    
    let customers: Customer[] = [];
    
    // Step 3: Get customer list based on user status
    if (userData.is_superuser) {
      // If superuser, fetch all customers using the API module
      const apiCustomers = await getCustomers();
      // Convert to expected Customer type (ensuring id is string)
      customers = apiCustomers.map(customer => ({
        id: String(customer.id),
        name: customer.name,
        logo: customer.logo,
        settings: customer.settings
      }));
    } else {
      // If regular user, extract customers from customer_access
      customers = userData.customer_access.map(access => {
        const customer = access.customer;
        return {
          id: String(customer.id),
          name: customer.name,
          logo: customer.logo,
          settings: customer.settings
        };
      });
    }

    // Store token expiry (must match backend ACCESS_TOKEN_LIFETIME minus buffer)
    const tokenExpiry = new Date();
    tokenExpiry.setMinutes(tokenExpiry.getMinutes() + TOKEN_EXPIRY_MINUTES);
    localStorage.setItem('token_expiry', tokenExpiry.toISOString());

    return {
      access,
      refresh,
      user,
      customers
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Implement retry mechanism for token refresh
export const refreshToken = async (refreshTokenValue: string, retryCount = 0, maxRetries = 5): Promise<{ access: string; refresh: string }> => {
  if (!refreshTokenValue) {
    console.error('Cannot refresh token: No refresh token provided');
    throw new Error('No refresh token provided');
  }

  try {
    console.log(`Attempting to refresh token (attempt ${retryCount + 1}/${maxRetries + 1})...`);
    
    // Use the auth API module for token refresh
    const response = await refreshAuthToken(refreshTokenValue);
    
    if (!response || !response.access || !response.refresh) {
      console.error('Token refresh failed: Invalid response format');
      throw new Error('Invalid refresh response format');
    }
    
    // Update token expiry (must match backend ACCESS_TOKEN_LIFETIME minus buffer)
    const tokenExpiry = new Date();
    tokenExpiry.setMinutes(tokenExpiry.getMinutes() + TOKEN_EXPIRY_MINUTES);
    localStorage.setItem('token_expiry', tokenExpiry.toISOString());
    
    // Store the new tokens immediately
    localStorage.setItem('access_token', response.access);
    localStorage.setItem('refresh_token', response.refresh);
    
    // Update API client authorization header with new token
    api.defaults.headers.common['Authorization'] = `Bearer ${response.access}`;
    
    // Dispatch event to notify about token refresh
    console.log('Token refreshed successfully');
    window.dispatchEvent(new Event('token-refreshed'));
    
    return { 
      access: response.access,
      refresh: response.refresh 
    };
  } catch (error: any) {
    console.error(`Token refresh error (attempt ${retryCount + 1}/${maxRetries + 1}):`, error);
    
    // Check specifically for invalid token error
    if (axios.isAxiosError(error) && error.response) {
      const errorData = error.response.data;
      const status = error.response.status;
      
      // Check for any token_not_valid error - includes blacklisted, expired, or invalid tokens
      const isInvalidToken =
        status === 401 || errorData?.code === 'token_not_valid';
      
      if (isInvalidToken) {
        console.error('Token is invalid or expired. Error detail:', errorData?.detail ?? errorData);
        // Caller (core.ts) clears storage, shows toast, and redirects on 401
        throw error;
      }
      
      // If token is invalid (other 401 or 400), don't retry
      if (status === 401 || status === 400) {
        console.error('Token is invalid, not retrying');
        throw error; // Let the calling function handle the invalid token error
      }
    }
    
    // Retry on server errors or network issues with backoff
    if (retryCount < maxRetries) {
      const delay = Math.min(2000 * Math.pow(1.5, retryCount), 10000); // Exponential backoff with cap
      console.log(`Retrying token refresh in ${delay}ms`);
      
      return new Promise((resolve, reject) => {
        setTimeout(async () => {
          try {
            const result = await refreshToken(refreshTokenValue, retryCount + 1, maxRetries);
            resolve(result);
          } catch (retryError) {
            reject(retryError);
          }
        }, delay);
      });
    }
    
    // If we've exhausted all retries, throw the error
    throw error;
  }
};

export const verifyToken = async (token: string): Promise<void> => {
  try {
    // Use the auth API module for token verification
    await verifyAuthToken(token);
  } catch (error) {
    console.error('Token verification error:', error);
    throw error;
  }
};

export const isTokenExpired = (): boolean => {
  const tokenExpiry = localStorage.getItem('token_expiry');
  
  if (!tokenExpiry) {
    return true;
  }
  
  const expiryDate = new Date(tokenExpiry);
  const now = new Date();
  
  // Return true if token has expired
  return now >= expiryDate;
};

/**
 * Check if the user is currently authenticated
 */
export const isAuthenticated = (): boolean => {
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  return !!accessToken && !!refreshToken;
};

// Make sure axios is imported
import axios from 'axios';
